import { css, ThemeProvider as EmotionThemeProvider } from '@emotion/react';
import { useContext, useState, useEffect, useMemo } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import EmailField from '../../components/EmailField';
import LanguageField from '../../components/LanguageField';
import LocaleField from '../../components/LocaleField';
import PageContainer from '../../components/PageContainer';
import Button from '../../components/Button';
import InputField from '../../components/InputField';
import TimezoneField from '../../components/TimezoneField';
import ResponseStatus from '../../constants/ResponseStatus';
import { navigateToErrorPage } from '../../services/errorUtils';
import convertRequestStateToSearchParams from '../../services/convertRequestStateToSearchParams';
import useMessageService from '../../services/i18n/useMessageService';
import UserStatus from '../../constants/UserStatus';
import useLanguageLocaleTimezoneValues from '../../services/useLanguageLocaleTimezoneValues';
import useLanguagesLocalesTimezones from '../../services/useLanguagesLocalesTimezones';
import RegisterPasswordSetter from './RegisterPasswordSetter';
import useNotificationCookie from '../../services/useNotificationCookie';
import useRequestState from '../../services/useRequestState';
import RegisterActiveUser from './RegisterActiveUser';
import UnauthenticatedRouteLanguageContext from '../../services/UnauthenticatedRouteLanguageContext';
import ErrorPageErrorTypes from '../../constants/ErrorPageErrorTypes';
import getTargetAttribute from '../../services/getTargetAttribute';
import LegalNameVerification from '../../components/LegalNameVerification';

const RegisterPage = () => {
    const notificationCookie = useNotificationCookie();
    const {
        firstName: cookieFirstName,
        lastName: cookieLastName,
        email,
        status,
    } = notificationCookie ?? {};

    const { setMessageLanguage } = useContext(UnauthenticatedRouteLanguageContext);
    const history = useHistory();
    const { search } = useLocation();
    const [showPasswordSetterScreen, setShowPasswordSetterScreen] = useState(false);
    const [{ languages, locales, timezones }, { localesResponse }, setLocaleOptions] =
        useLanguagesLocalesTimezones(notificationCookie?.language);
    const { language, setLanguage, locale, setLocale, timezone, setTimezone } =
        useLanguageLocaleTimezoneValues(
            notificationCookie,
            locales,
            localesResponse?.data?.isLanguageLocaleDecoupled,
        );
    const [firstName, setFirstName] = useState(cookieFirstName);
    const [lastName, setLastName] = useState(cookieLastName);
    const [legalNameVerified, setLegalNameVerified] = useState(false);
    const requestState = useRequestState();

    const [
        [
            pageTitle = "Let's Start Your Registration",
            usernameLabel = `Username (Your email)`,
            firstNameLabel = 'First Name',
            lastNameLabel = 'Last Name',
            continueLabel = 'Continue',
            languageLabel = 'Language',
            localeLabel = 'Locale',
            timezoneLabel = 'Timezone',
        ],
    ] = useMessageService([
        'register.start_registration',
        'register.username_label',
        'general.first_name',
        'general.last_name',
        'general.continue',
        'general.language',
        'general.locale',
        'general.timezone',
    ]);

    const shouldGoToErrorPage = useMemo(() => {
        return !notificationCookie && !showPasswordSetterScreen;
    }, [notificationCookie, showPasswordSetterScreen]);

    useEffect(() => {
        const localesResponseContainsErrors = [
            ResponseStatus.FAILURE,
            ResponseStatus.UI_NETWORK_ERROR,
        ].includes(localesResponse?.responseStatus);

        if (shouldGoToErrorPage) {
            navigateToErrorPage(history, { type: ErrorPageErrorTypes.GENERIC_ERROR });
        } else if (localesResponseContainsErrors) {
            navigateToErrorPage(history, { type: ErrorPageErrorTypes.FAILED_TO_LOAD });
        }
        // history is intentionally ignored because it rapidly changes and those changes do not affect navigation logic
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [shouldGoToErrorPage, localesResponse]);

    useEffect(() => {
        const searchContainsPasswordScreen = search.includes('passwordscreen=true');
        if (!showPasswordSetterScreen && searchContainsPasswordScreen) {
            // user tries to go straight to /register?passwordscreen=true
            history.replace({ pathname: '/register' });
        } else if (showPasswordSetterScreen && !searchContainsPasswordScreen) {
            // user tries to go back to /register after already being on the PasswordSetter screen
            setShowPasswordSetterScreen(false);
        }
        setLegalNameVerified(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [history, search, showPasswordSetterScreen]);

    const goToPasswordScreen = () => {
        setShowPasswordSetterScreen(true);
        setFirstName(firstName?.trim());
        setLastName(lastName?.trim());
        setMessageLanguage(language);
        const searchString = `?passwordscreen=true&${convertRequestStateToSearchParams(
            requestState,
        )}`;
        history.push({ pathname: '/register', search: searchString });
    };

    const onFirstNameChange = (value) => {
        setFirstName(value);
    };

    const onLastNameChange = (value) => {
        setLastName(value);
    };

    const handleLanguageChange = async (lang) => {
        if (localesResponse.data.isLanguageLocaleDecoupled) {
            setLanguage(lang);
        } else {
            await setLocaleOptions(lang);
            setLanguage(lang);
            setLocale('');
        }
    };

    const handleLocaleChange = (value) => {
        setLocale(value);
    };

    const handleTimezoneChange = (value) => {
        setTimezone(value);
    };

    const handleLegalNameVerifiedChange = (value) => {
        setLegalNameVerified(value);
    };

    const fieldsIncomplete = [firstName, lastName, language, locale, timezone].some(
        (it) => !it || it?.trim().length < 1,
    );

    const buttonDisabled = fieldsIncomplete || !legalNameVerified;

    if (status === UserStatus.EXISTING) {
        return <RegisterActiveUser username={email} firstName={firstName} lastName={lastName} />;
    }

    if (showPasswordSetterScreen) {
        return (
            <RegisterPasswordSetter
                username={email}
                firstName={firstName}
                lastName={lastName}
                language={language}
                locale={locale}
                timezone={timezone}
            />
        );
    }

    return (
        <PageContainer title={pageTitle}>
            <EmotionThemeProvider theme={{ veevaIdErrorLabelBufferHeight: '1.66667rem' }}>
                <EmailField value={email} readOnly label={usernameLabel} hideIcon />
                <InputField
                    onChange={onFirstNameChange}
                    value={firstName}
                    label={firstNameLabel}
                    data-target={getTargetAttribute('first-name-field')}
                />
                <InputField
                    onChange={onLastNameChange}
                    value={lastName}
                    label={lastNameLabel}
                    data-target={getTargetAttribute('last-name-field')}
                />
                <LanguageField
                    label={languageLabel}
                    languages={languages}
                    onChange={handleLanguageChange}
                    value={language}
                />
                <LocaleField
                    label={localeLabel}
                    locales={locales}
                    onChange={handleLocaleChange}
                    value={locale}
                />
                <TimezoneField
                    label={timezoneLabel}
                    onChange={handleTimezoneChange}
                    timezones={timezones}
                    value={timezone}
                />
                <LegalNameVerification
                    legalNameVerified={legalNameVerified}
                    onChange={handleLegalNameVerifiedChange}
                />
                <Button
                    onClick={goToPasswordScreen}
                    disabled={buttonDisabled}
                    css={css`
                        margin-top: 1.6666rem;
                    `}
                    data-target={getTargetAttribute('submit-button')}
                >
                    {continueLabel ?? ' '}
                </Button>
            </EmotionThemeProvider>
        </PageContainer>
    );
};

export default RegisterPage;
